import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { History, Loader2, CheckCircle2, AlertTriangle, XCircle, RefreshCw, ChevronDown } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/os-patch`;

/**
 * SyncHistoryCard
 * ---------------
 * Vertical timeline of the most recent sync runs (manual + nightly) for
 * this tenant. Sourced from per-tenant `sync_history` collection — written
 * to by both the manual `/sync/all` background job and the nightly
 * scheduler.
 *
 * Pure read-only — gives operators visibility into what their nightly is
 * doing over time without opening Cosmos DB Explorer.
 */
export default function SyncHistoryCard() {
  const [loading, setLoading] = useState(true);
  const [entries, setEntries] = useState([]);
  const [refreshing, setRefreshing] = useState(false);
  // `scott/kpi-cycle-update2` — collapsed by default. The timeline is 20 runs
  // deep and pushed everything else on Data Management below the fold; it is
  // reference material you go looking for, not a dashboard.
  const [expanded, setExpanded] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/sync-history?limit=20`);
      setEntries(res.data?.entries || []);
    } catch (e) {
      console.error('Failed to load sync history', e);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const onRefresh = () => {
    setRefreshing(true);
    load();
  };

  const statusBadge = (s) => {
    const map = {
      success: { Icon: CheckCircle2, cls: 'text-emerald-600', label: 'Success' },
      partial: { Icon: AlertTriangle, cls: 'text-amber-600', label: 'Partial' },
      error: { Icon: XCircle, cls: 'text-red-600', label: 'Error' },
    };
    const v = map[s] || map.success;
    const { Icon } = v;
    return (
      <span className={`inline-flex items-center gap-1 text-xs font-medium ${v.cls}`}>
        <Icon className="h-3.5 w-3.5" />
        {v.label}
      </span>
    );
  };

  const fmtDate = (iso) => {
    if (!iso) return '—';
    try {
      const d = new Date(iso);
      return d.toLocaleString(undefined, {
        month: 'short', day: 'numeric',
        hour: '2-digit', minute: '2-digit',
      });
    } catch { return iso; }
  };

  const fmtDuration = (sec) => {
    if (sec == null) return '';
    if (sec < 60) return `${Math.round(sec)} s`;
    return `${Math.floor(sec / 60)} m ${Math.round(sec % 60)} s`;
  };

  // Per-step pill: green/amber/red dot + step name
  const stepPill = (name, step) => {
    const status = step?.status || 'unknown';
    const dot = {
      success: 'bg-emerald-500',
      partial: 'bg-amber-500',
      error: 'bg-red-500',
      skipped: 'bg-slate-400',
      pending: 'bg-slate-300',
      no_data: 'bg-slate-400',
    }[status] || 'bg-slate-300';
    let tooltip = `${name}: ${status}`;
    if (step?.count != null) tooltip += ` (${step.count})`;
    if (step?.entries_synced != null) tooltip += ` (${step.entries_synced} new / ${step.entries_updated || 0} updated)`;
    if (step?.reason) tooltip += ` — ${step.reason}`;
    if (step?.error) tooltip += ` — ${step.error}`;
    return (
      <span key={name} className="inline-flex items-center gap-1 text-xs" title={tooltip}>
        <span className={`inline-block w-1.5 h-1.5 rounded-full ${dot}`} />
        <span className="text-muted-foreground">{name}</span>
      </span>
    );
  };

  return (
    <Card data-testid="sync-history-card">
      <CardHeader className="flex flex-row items-start justify-between">
        {/* A div rather than a <button>: CardDescription renders a <p>, which
            is flow content and is not valid inside a button. */}
        <div
          role="button"
          tabIndex={0}
          onClick={() => setExpanded((v) => !v)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setExpanded((v) => !v); }
          }}
          className="text-left cursor-pointer select-none"
          data-testid="sync-history-toggle"
          aria-expanded={expanded}
        >
          <CardTitle className="flex items-center gap-2 font-['Chivo']">
            <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${expanded ? '' : '-rotate-90'}`} />
            <History className="h-5 w-5" />
            Sync History
            {!loading && entries.length > 0 && (
              <span className="text-xs font-normal text-muted-foreground">
                · last run {fmtDate(entries[0]?.completed_at || entries[0]?.started_at)}
              </span>
            )}
          </CardTitle>
          <CardDescription>
            Last {entries.length || '0'} sync runs (manual + nightly) for this
            tenant. Per-step status: green = success, amber = partial,
            red = error, grey = skipped.
          </CardDescription>
        </div>
        {expanded && (
          <Button
            variant="outline"
            size="sm"
            onClick={onRefresh}
            disabled={refreshing}
            data-testid="refresh-sync-history-btn"
            className="bg-[#E5EFFA] text-[#3C02CC] hover:bg-[#BBFF71] hover:text-[#3C02CC] font-semibold border-none"
          >
            {refreshing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          </Button>
        )}
      </CardHeader>
      {expanded && (
      <CardContent>
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading sync history…
          </div>
        ) : entries.length === 0 ? (
          <div className="text-sm text-muted-foreground py-4 text-center">
            No sync runs recorded yet. Sync history is populated after the
            first manual or nightly sync completes.
          </div>
        ) : (
          <div className="divide-y" data-testid="sync-history-list">
            {entries.map((e, idx) => (
              <div key={idx} className="py-3 first:pt-0 last:pb-0">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-3">
                    {statusBadge(e.status)}
                    <span className="text-sm font-medium">
                      {e.trigger === 'nightly_scheduler' ? 'Nightly' : 'Manual'}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {fmtDate(e.started_at)}
                    </span>
                    {e.duration_seconds != null && (
                      <span className="text-xs text-muted-foreground">
                        · {fmtDuration(e.duration_seconds)}
                      </span>
                    )}
                  </div>
                </div>
                {e.steps && (
                  <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1">
                    {Object.entries(e.steps).map(([name, step]) => stepPill(name, step))}
                  </div>
                )}
                {e.error && (
                  <div className="mt-1 text-xs text-red-600 truncate" title={e.error}>
                    {e.error.length > 200 ? e.error.slice(0, 200) + '…' : e.error}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
      )}
    </Card>
  );
}
